import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useLocation } from "@tanstack/react-router";
import type { LangCode } from "./translate.functions";
import { LOCALES, LOCALE_LABELS, isLocale, localeDir, localePath, splitLocalePath, type Locale } from "./locale";

export const LANGUAGES: Array<{ code: LangCode; flag: string; label: string; short: string }> =
  LOCALES.map((code) => ({ code, ...LOCALE_LABELS[code] }));

const STORAGE_KEY = "rt:lang";

type Ctx = {
  lang: LangCode;
  setLang: (lang: LangCode) => void;
  translating: boolean;
};

const LanguageContext = createContext<Ctx>({
  lang: "en",
  setLang: () => {},
  translating: false,
});

export function useLanguage() {
  return useContext(LanguageContext);
}

function isLang(value: string | null): value is LangCode {
  return isLocale(value);
}

export function LanguageProvider({ children }: { children: ReactNode }) {
  // Always "en" for the first render so server and client HTML match.
  const [lang, setLangState] = useState<LangCode>("en");
  const [translating, setTranslating] = useState(false);
  const location = useLocation();
  const langRef = useRef<LangCode>("en");
  langRef.current = lang;

  // After hydration, the address decides the language: /de/… is German. Only
  // when there is no language in the address does the remembered choice apply.
  useEffect(() => {
    try {
      const fromUrl = splitLocalePath(window.location.pathname).locale;
      if (fromUrl !== "en") {
        setLangState(fromUrl);
        return;
      }
      const stored = localStorage.getItem(STORAGE_KEY);
      if (isLang(stored) && stored !== "en") setLangState(stored);
    } catch {
      /* storage unavailable */
    }
  }, []);

  // Changing language changes the address, so the page can be shared, indexed
  // and reopened in that language. The choice is always the visitor's — nobody
  // is ever redirected by country.
  const setLang = useCallback((next: LangCode) => {
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      /* storage unavailable */
    }

    if (typeof window !== "undefined") {
      const { path } = splitLocalePath(window.location.pathname);
      const target = `${localePath(next, path)}${window.location.search}${window.location.hash}`;
      if (target !== window.location.pathname + window.location.search + window.location.hash) {
        window.location.assign(target);
        return;
      }
    }

    setLangState(next);
  }, []);

  // Reflect the active language and its reading direction for assistive tech
  // and for the layout itself: Arabic mirrors the whole interface.
  useEffect(() => {
    document.documentElement.lang = lang;
    document.documentElement.dir = localeDir(lang as Locale);
  }, [lang]);

  // Run a translation pass on language change, route change, and whenever new
  // content appears (modals, dialogs, lazily loaded sections, toasts).
  useEffect(() => {
    let disposed = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let observer: MutationObserver | undefined;

    if (lang === "en") {
      void import("./translate-dom").then((m) => {
        m.cancelPendingTranslation();
        m.restoreOriginals();
      });
      return;
    }

    const run = async () => {
      const m = await import("./translate-dom");
      if (disposed || langRef.current === "en") return;
      setTranslating(true);
      try {
        await m.translateTree(document.body, lang as Exclude<LangCode, "en">);
      } finally {
        if (!disposed) setTranslating(false);
      }
    };

    // Debounce bursts of DOM changes, but never let a page that animates
    // continuously (carousels, tickers) postpone translation forever.
    let pendingSince = 0;
    const schedule = () => {
      const now = Date.now();
      if (!pendingSince) pendingSince = now;
      if (timer) clearTimeout(timer);
      const wait = now - pendingSince >= 800 ? 0 : 120;
      timer = setTimeout(() => {
        pendingSince = 0;
        void run();
      }, wait);
    };

    schedule();

    observer = new MutationObserver((records) => {
      const meaningful = records.some(
        (r) => r.addedNodes.length > 0 || r.type === "characterData",
      );
      if (meaningful) schedule();
    });
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      characterData: true,
    });

    return () => {
      disposed = true;
      if (timer) clearTimeout(timer);
      observer?.disconnect();
    };
  }, [lang, location.pathname]);

  return (
    <LanguageContext.Provider value={{ lang, setLang, translating }}>
      {children}
    </LanguageContext.Provider>
  );
}
